/**
 * Report graphics helpers
 *
 * Produces raster (PNG data URL) versions of the dashboard iconography so they
 * can be embedded in a jsPDF document (jsPDF cannot render SVG or React
 * components directly). Each icon is drawn as a white Material-style glyph on a
 * solid colored circular badge, matching the on-screen iconography.
 */

// ── Icon palette + glyphs ────────────────────────────────────────────────────
// Glyph path data uses a 24×24 viewBox.
// Paths may be plain strings (d attribute only) or objects { d, fillRule, clipRule }
// when fill-rule/clip-rule attributes are needed (e.g. evenodd cut-outs).
// Icon artwork sourced from Figma design assets (Export-Button-Assets/).
const ICON_DEFS = {
    // Alert / activity categories
    motion: {
        // Source: Export-Button-Assets/run.svg
        color: '#F59E0B',
        paths: [
            'M14.8634 1.83471C15.9048 1.83471 16.8469 2.54132 17.0948 3.60744C17.38 4.83471 16.6238 6.07438 15.3841 6.3595C15.2106 6.39669 15.037 6.42149 14.8634 6.42149C13.8221 6.42149 12.88 5.71488 12.632 4.64876C12.3469 3.42149 13.1031 2.18182 14.3428 1.89669C14.5163 1.8595 14.6899 1.83471 14.8634 1.83471ZM11.6031 6.50826C11.9254 6.50826 12.2601 6.57025 12.5577 6.68182L14.5163 7.38843C14.975 7.54959 15.3841 7.83471 15.694 8.20661L16.8717 9.58264L18.2973 8.72727C18.4709 8.6157 18.6692 8.56611 18.8552 8.56611C19.2271 8.56611 19.5866 8.75207 19.7849 9.09917C20.0949 9.61983 19.9337 10.2893 19.413 10.5992L17.194 11.938C17.0205 12.0372 16.8221 12.0992 16.6362 12.0992C16.3263 12.0992 16.0163 11.9628 15.8056 11.7149L14.5659 10.2645L13.3139 13.5372L15.6444 15.4339C15.8676 15.6074 16.0163 15.8554 16.0907 16.1281L17.194 20.5537C17.3676 21.2603 16.9461 21.9669 16.2395 22.1405C16.1279 22.1653 16.0287 22.1777 15.9172 22.1777C15.3345 22.1777 14.8015 21.781 14.6527 21.1859L13.661 17.1818L11.975 15.8058C11.975 15.8058 11.2312 17.3554 10.9585 17.9256C10.6858 18.4959 10.2891 18.8678 9.70642 18.8678H5.31799C4.59898 18.8678 4.01634 18.2851 4.01634 17.5661C4.01634 16.8471 4.59898 16.2645 5.31799 16.2645H8.72708L9.7932 13.9959C9.50807 13.6736 9.3965 13.2273 9.50807 12.8058L10.4998 9L9.28493 9.3843L8.39237 11.3678C8.20642 11.7769 7.80973 12.0124 7.40064 12.0124C7.25187 12.0124 7.10311 11.9876 6.95435 11.9132C6.4089 11.6653 6.16097 11.0207 6.4089 10.4752L7.49981 8.04545C7.62378 7.76033 7.87171 7.54959 8.16923 7.45041L10.7353 6.63223C11.0081 6.54545 11.3056 6.49587 11.5907 6.49587M14.8634 0C14.5411 0 14.2312 0.0371901 13.9213 0.11157C11.8139 0.607438 10.4626 2.6405 10.7849 4.74793C10.5866 4.78512 10.4006 4.83471 10.2023 4.89669L7.63617 5.71488C6.84278 5.96281 6.19816 6.54545 5.85105 7.30165L4.76014 9.7314C4.10311 11.2066 4.76014 12.9298 6.23535 13.5992C6.60725 13.7727 7.01634 13.8595 7.42543 13.8595C7.5246 13.8595 7.63617 13.8595 7.73535 13.8471C7.74774 13.9091 7.76014 13.9711 7.78493 14.0207L7.58659 14.4421H5.34278C3.60725 14.4421 2.20642 15.8554 2.20642 17.5785C2.20642 19.3017 3.61964 20.7149 5.34278 20.7149H9.73121C10.7849 20.7149 11.7023 20.2066 12.3221 19.2769L12.9048 21.6198C13.2519 23.0207 14.5039 24 15.9544 24C16.2147 24 16.4626 23.9628 16.7106 23.9132C18.3965 23.4917 19.4254 21.781 19.0039 20.1074L17.9006 15.6818C17.7395 15.0248 17.3552 14.4298 16.8345 14.0083L16.7229 13.9215C17.2312 13.9215 17.7271 13.7603 18.161 13.5L20.38 12.1612C21.0494 11.7645 21.5205 11.1198 21.7064 10.3636C21.8924 9.60744 21.7808 8.81405 21.3717 8.15702C20.851 7.27686 19.8841 6.7438 18.8676 6.7438C18.5453 6.7438 18.2229 6.79339 17.913 6.90496C18.7808 5.93802 19.1775 4.56198 18.8552 3.21074C18.4213 1.33884 16.7601 0.0247934 14.8387 0.0247934L14.8634 0Z',
        ],
    },
    smart_home: {
        // Source: Export-Button-Assets/smarthome.svg
        color: '#0EA5E9',
        paths: [
            { d: 'M13.8847 3.14813L22.1317 10.0103H22.0904C22.5037 10.341 22.7518 10.8164 22.7931 11.3435C22.8344 11.8395 22.6898 12.3253 22.3591 12.7386L21.646 13.5861C21.6408 13.5913 21.6356 13.599 21.6305 13.6068C21.6253 13.6145 21.6201 13.6223 21.615 13.6274C21.3049 13.9581 20.9019 14.1752 20.4472 14.2475C20.3748 14.2682 20.2611 14.2682 20.1785 14.2682H20.1578V19.2288C20.1578 19.8385 19.9201 20.4069 19.486 20.841C19.052 21.275 18.4836 21.5127 17.8738 21.5127H13.461C12.7789 21.5127 12.2001 21.0683 11.9934 20.4586C11.7868 21.0683 11.208 21.5127 10.5259 21.5127H6.11305C5.50331 21.5127 4.93491 21.275 4.50085 20.841C4.0668 20.4069 3.8291 19.8282 3.8291 19.2184V14.2578H3.80844H3.65342C3.62241 14.2475 3.58107 14.2475 3.55007 14.2475C3.08501 14.1855 2.68196 13.9581 2.35126 13.5861L1.6175 12.7076C1.3178 12.3253 1.17311 11.8499 1.20411 11.3538C1.24545 10.8267 1.49348 10.341 1.8862 10.031L10.1642 3.1378C10.6809 2.71408 11.3217 2.48672 12.0141 2.48672C12.7065 2.48672 13.3473 2.71408 13.8847 3.14813ZM12.9029 4.33661C12.6549 4.14025 12.3655 4.03691 12.0244 4.03691C11.6834 4.03691 11.394 4.14025 11.146 4.33661L2.88866 11.2298C2.81631 11.2918 2.76464 11.3745 2.76464 11.4778C2.75431 11.5812 2.78531 11.6742 2.84732 11.7465L3.56041 12.594C3.62241 12.656 3.69475 12.7076 3.7981 12.718H3.8291C3.91178 12.718 3.99446 12.687 4.07714 12.6353L12.0348 6.00048L19.9924 12.6353C20.0544 12.687 20.1268 12.718 20.2301 12.718H20.2611C20.3541 12.7076 20.4368 12.6663 20.4988 12.594L21.2119 11.7465L21.2222 11.7362C21.2843 11.6638 21.3049 11.5708 21.3049 11.4675C21.3049 11.3641 21.2533 11.2815 21.1809 11.2195L12.9029 4.33661ZM18.4319 19.7455C18.5766 19.6008 18.6489 19.4251 18.6489 19.2288V13.7204C18.6489 13.6894 18.6386 13.6481 18.6386 13.6481L12.0244 8.20175L5.4103 13.6481V13.6791V13.7101V19.2288C5.4103 19.4251 5.48264 19.6008 5.62733 19.7455C5.77201 19.8902 5.9477 19.9625 6.14406 19.9625H10.5569V16.5H13.5023V19.9625H17.9152C18.1115 19.9625 18.2872 19.8902 18.4319 19.7455Z', fillRule: 'evenodd', clipRule: 'evenodd' },
            { d: 'M12.0616 17.2769C13.5077 17.2769 14.6769 16.1077 14.6769 14.6616C14.6769 13.2154 13.5077 12.0462 12.0616 12.0462C10.6154 12.0462 9.44617 13.2154 9.44617 14.6616C9.44617 16.1077 10.6154 17.2769 12.0616 17.2769ZM11.2205 14.6616C11.2205 15.1231 11.6 15.5026 12.0616 15.5026C12.5231 15.5026 12.9026 15.1231 12.9026 14.6616C12.9026 14.2 12.5231 13.8205 12.0616 13.8205C11.6 13.8205 11.2205 14.2 11.2205 14.6616Z', fillRule: 'evenodd', clipRule: 'evenodd' },
            'M18.524 0.772685C18.2114 0.760375 17.9647 0.972227 17.977 1.20873L17.9688 1.20359C17.9842 1.45347 18.2105 1.64059 18.4665 1.65188C20.1786 1.74941 21.7171 2.71072 22.5553 4.2068C22.6859 4.43713 22.9532 4.55844 23.1764 4.45765C23.3995 4.35685 23.4817 4.04219 23.3285 3.77485C22.3453 2.01656 20.5271 0.880468 18.524 0.772685Z',
            'M18.153 3.16041C18.1324 2.91877 18.4142 2.70588 18.7545 2.74698C19.8487 2.88159 20.8359 3.49847 21.4365 4.42285C21.6226 4.71075 21.5548 5.05729 21.3286 5.14472C21.1024 5.23215 20.8432 5.11597 20.7023 4.9021C20.2426 4.1916 19.4858 3.71865 18.6456 3.61693C18.3916 3.58405 18.1736 3.40206 18.153 3.16041Z',
        ],
    },
    biometric: {
        // Source: Export-Button-Assets/general care.svg
        color: '#EF4444',
        paths: [
            'M15.6107 4.96271C16.2535 4.96271 16.8635 5.09566 17.4197 5.33134C17.9729 5.56703 18.4753 5.91149 18.8969 6.33753C19.3155 6.76357 19.6564 7.26516 19.8926 7.83019C20.1258 8.38918 20.2574 9.00861 20.2574 9.65523C20.2574 10.3018 20.1258 10.9213 19.8926 11.4833C19.6564 12.0423 19.3155 12.5499 18.8969 12.9759C18.4753 13.399 12.5577 18.8016 12.0045 19.0403C11.4483 18.8016 5.53372 13.399 5.1121 12.9759C4.69049 12.5499 4.35259 12.0453 4.11637 11.4833C3.88313 10.9213 3.75157 10.3049 3.75157 9.65523C3.75157 9.00559 3.88313 8.39221 4.11637 7.83019C4.3496 7.26818 4.69049 6.76357 5.1121 6.33753C5.53372 5.91149 6.03009 5.57005 6.58926 5.33134C7.14244 5.09566 7.75543 4.96271 8.39533 4.96271C9.03523 4.96271 9.64822 5.12587 10.2044 5.41293C10.7576 5.69695 11.2599 6.10789 11.6815 6.59739C11.7952 6.73336 11.9058 6.87235 12.0075 7.02041C12.1091 6.87537 12.2198 6.73336 12.3364 6.59739C12.758 6.10789 13.2574 5.69998 13.8136 5.41293C14.3697 5.12587 14.9797 4.96271 15.6196 4.96271M15.6107 3.59998C14.7764 3.59998 13.9631 3.80242 13.1916 4.19825C12.77 4.4158 12.3723 4.68472 12.0015 5.00803C11.6307 4.68775 11.233 4.4158 10.8114 4.19825C10.0429 3.80242 9.2266 3.59998 8.38935 3.59998C7.5521 3.59998 6.79857 3.76012 6.05999 4.07739C5.34833 4.37954 4.70843 4.81767 4.15524 5.37364C3.60206 5.93264 3.17147 6.57926 2.86946 7.29839C2.55848 8.04775 2.4 8.8394 2.4 9.65221C2.4 10.465 2.55848 11.2567 2.86946 12.009C3.17446 12.7342 3.60505 13.3808 4.15225 13.9368C4.21206 13.9972 10.5303 19.8863 11.4692 20.2912C11.6397 20.3637 11.8191 20.4 11.9985 20.4C12.1779 20.4 12.3603 20.3637 12.5308 20.2912C13.4816 19.8803 19.7879 13.9972 19.8418 13.9398C20.392 13.3808 20.8226 12.7372 21.1246 12.0181C21.4415 11.2597 21.6 10.468 21.6 9.65523C21.6 8.84242 21.4415 8.05077 21.1276 7.30141C20.8255 6.58228 20.395 5.93566 19.8478 5.37969C19.2976 4.82372 18.6547 4.38559 17.943 4.08041C17.1984 3.76314 16.412 3.603 15.6077 3.603L15.6107 3.59998Z',
        ],
    },
    calls: {
        // Source: Export-Button-Assets/call.svg
        color: '#3B82F6',
        paths: [
            'M19.7798 13.9384C18.8294 12.8115 16.5361 12.429 15.6374 12.429C15.3688 12.429 15.1415 12.4497 14.9246 12.5014C14.2325 12.6461 13.7159 13.0803 13.3131 13.4939C12.2077 12.7392 11.247 11.788 10.4929 10.6818C10.9061 10.2786 11.34 9.7617 11.4846 9.06902C11.7325 7.89044 11.185 5.1921 10.059 4.22028C9.60449 3.83776 8.98468 3.59998 8.40618 3.59998H8.36486H6.18517C5.45172 3.59998 4.76992 3.91013 4.27406 4.44773C3.81953 4.95431 3.5716 5.61598 3.6026 6.28798C3.61293 10.1339 5.0695 13.6903 7.69339 16.3163C10.307 18.9319 13.8399 20.3793 17.6415 20.4C17.6931 20.4 17.7448 20.4 17.7861 20.4C18.4679 20.4 19.0774 20.1622 19.5629 19.7176C19.7282 19.5626 19.8728 19.3868 19.9968 19.2007C20.255 18.7975 20.3996 18.3219 20.3996 17.836V15.6339C20.41 15.0343 20.193 14.414 19.7902 13.9384H19.7798ZM18.6022 17.836C18.6022 18.0428 18.5092 18.2496 18.3439 18.4047C18.1993 18.5391 18.0133 18.6114 17.7861 18.6114C17.7551 18.6114 17.7241 18.6114 17.6931 18.6114C14.3564 18.6114 11.247 17.3501 8.96402 15.055C6.66036 12.7495 5.38974 9.63764 5.38974 6.22594C5.36907 6.01918 5.44139 5.80207 5.59634 5.64699C5.7513 5.48158 5.9579 5.38853 6.16451 5.38853H8.37519C8.37519 5.38853 8.38552 5.38853 8.39585 5.38853C8.5508 5.38853 8.75741 5.4609 8.8917 5.57462C9.41855 6.02951 9.89374 7.98348 9.73879 8.69684C9.66648 9.02767 9.30491 9.3585 8.95369 9.67899C8.82972 9.79271 8.71609 9.8961 8.60246 10.0098C8.38552 10.2269 8.33387 10.5681 8.48882 10.8472C9.59416 12.7909 11.2057 14.4037 13.1581 15.5099C13.2614 15.5719 13.3854 15.6029 13.499 15.6029C13.6746 15.6029 13.8606 15.5306 13.9949 15.4065C14.0982 15.2928 14.2118 15.1791 14.3254 15.055C14.6457 14.7035 14.9762 14.3416 15.3068 14.2693C15.3998 14.2486 15.5134 14.2383 15.6477 14.2383C16.5155 14.2383 18.0443 14.6518 18.4369 15.117C18.5609 15.2618 18.6228 15.4479 18.6228 15.6546V17.8567L18.6022 17.836Z',
        ],
    },
    engagement: {
        // Source: Export-Button-Assets/favorite.svg
        color: '#8B5CF6',
        paths: [
            'M17.3734 19.9925C17.2289 19.9925 17.0948 19.9616 16.9607 19.9L11.9977 17.4225L7.03474 19.9C6.71488 20.0644 6.32279 20.0233 6.03389 19.7972C5.7553 19.571 5.62117 19.2009 5.71403 18.8514L6.94187 13.9685L3.2377 9.85661C3.00039 9.59962 2.93848 9.21927 3.0623 8.89031C3.18611 8.56136 3.49565 8.33521 3.85678 8.31465L9.13961 7.91373L11.1516 3.49343C11.4509 2.83552 12.5446 2.83552 12.8438 3.49343L14.8558 7.90345L20.1386 8.30437C20.4894 8.33521 20.799 8.55108 20.9331 8.88003C21.0673 9.20899 20.995 9.57906 20.7577 9.84633L17.0535 13.948L18.2814 18.8309C18.3742 19.1804 18.2401 19.5504 17.9615 19.7766C17.7964 19.9102 17.5901 19.9822 17.3837 19.9822L17.3734 19.9925ZM11.9977 15.4591C12.1422 15.4591 12.2866 15.4899 12.4104 15.5516L15.9805 17.33L15.1241 13.9171C15.0518 13.619 15.1241 13.3003 15.3304 13.0742L18.106 10.0005L14.1542 9.70242C13.8137 9.67158 13.5248 9.46598 13.3803 9.16787L11.9874 6.10449L10.5945 9.16787C10.45 9.47626 10.1611 9.68186 9.8206 9.70242L5.87912 10.0005L8.65467 13.0742C8.86103 13.3003 8.94357 13.619 8.86103 13.9171L8.00463 17.33L11.5747 15.5516C11.7088 15.4899 11.8429 15.4591 11.9874 15.4591H11.9977Z',
        ],
    },

    // Call statuses
    completed: {
        color: '#22C55E',
        paths: [
            'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
        ],
    },
    missed: {
        color: '#EF4444',
        paths: ['M19.59 7L12 14.59 6.41 9H11V7H3v8h2v-4.59l7 7 9-9z'],
    },
    declined: {
        color: '#F97316',
        paths: [
            'M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm0 3c1.85 0 3.55.63 4.9 1.69L6.69 16.9C5.63 15.55 5 13.85 5 12c0-3.87 3.13-7 7-7zm0 14c-1.85 0-3.55-.63-4.9-1.69L17.31 7.1C18.37 8.45 19 10.15 19 12c0 3.87-3.13 7-7 7z',
        ],
    },
    failed: {
        color: '#DC2626',
        paths: [
            'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z',
        ],
    },
    scheduled: {
        color: '#005D98',
        paths: [
            'M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z',
        ],
    },
    ongoing: {
        color: '#113E57',
        paths: [
            'M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z',
        ],
    },
    incoming: {
        // Source: Export-Button-Assets/down left.svg (arrow toward bottom-left = incoming)
        color: '#22C55E',
        paths: [
            'M7.2923 7.84244C8.02592 7.86403 8.59772 8.45741 8.59774 9.19103L8.59782 12.7297L15.4052 5.92227C15.9338 5.39365 16.7753 5.39367 17.304 5.92232C17.8326 6.45096 17.8326 7.29246 17.304 7.82109L10.4966 14.6285L14.0353 14.6285C14.7689 14.6286 15.3515 15.2112 15.3838 15.934C15.4054 16.6892 14.8013 17.3149 14.0461 17.3149L5.93317 17.3147L5.93298 9.20176C5.93297 8.44656 6.55869 7.84242 7.31388 7.86402L7.2923 7.84244Z',
        ],
    },
    outgoing: {
        // Source: Export-Button-Assets/up right.svg (arrow toward top-right = outgoing)
        color: '#005D98',
        paths: [
            'M15.9556 15.4054C15.222 15.3838 14.6502 14.7905 14.6502 14.0568L14.6501 10.5182L7.8427 17.3256C7.31407 17.8542 6.47257 17.8542 5.94392 17.3255C5.41528 16.7969 5.41526 15.9554 5.94388 15.4268L12.7513 8.6194L9.21264 8.61932C8.47902 8.6193 7.89643 8.03671 7.86405 7.31388C7.84245 6.55868 8.44659 5.93297 9.20179 5.93298L17.3147 5.93317L17.3149 14.0461C17.3149 14.8013 16.6892 15.4054 15.934 15.3838L15.9556 15.4054Z',
        ],
    },
};

/** Icon keys grouped for callers, so consumers can request the exact set. */
export const REPORT_ICON_KEYS = Object.keys(ICON_DEFS);

/**
 * Maps an alert `message_type` to a category icon key.
 * @param {string} messageType
 * @returns {string}
 */
export const getAlertIconKey = (messageType) => {
    switch (messageType) {
        case 'insights_motion_alert_event':
            return 'motion';
        case 'insights_smart_home_alert_event':
            return 'smart_home';
        case 'insights_biometrics_alert_event':
            return 'biometric';
        default:
            return 'biometric';
    }
};

const buildBadgeSvg = ({ color, paths }) => {
    const scale = 0.62;
    const size = 48;
    const translate = (size - 24 * scale) / 2;
    const glyphs = paths.map((p) => {
        if (typeof p === 'string') return `<path d="${p}"/>`;
        const fr = p.fillRule ? ` fill-rule="${p.fillRule}"` : '';
        const cr = p.clipRule ? ` clip-rule="${p.clipRule}"` : '';
        return `<path d="${p.d}"${fr}${cr}/>`;
    }).join('');
    return (
        `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
        `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" fill="${color}"/>` +
        `<g transform="translate(${translate},${translate}) scale(${scale})" fill="#ffffff">${glyphs}</g>` +
        `</svg>`
    );
};

const loadImage = (src, crossOrigin) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        if (crossOrigin) img.crossOrigin = crossOrigin;
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

/**
 * Rasterizes a single badge icon definition to a PNG data URL.
 * @param {{color:string, paths:string[]}} def
 * @param {number} pixels Output resolution (square)
 * @returns {Promise<string|null>}
 */
const rasterizeBadge = async (def, pixels = 96) => {
    try {
        const svg = buildBadgeSvg(def);
        const src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
        const img = await loadImage(src);
        const canvas = document.createElement('canvas');
        canvas.width = pixels;
        canvas.height = pixels;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, pixels, pixels);
        return canvas.toDataURL('image/png');
    } catch (error) {
        return null;
    }
};

/**
 * Builds a map of every report icon keyed by name, each a PNG data URL.
 * Missing/failed icons resolve to `null`.
 * @returns {Promise<Record<string, string|null>>}
 */
export const buildReportIcons = async () => {
    const entries = await Promise.all(
        Object.entries(ICON_DEFS).map(async ([key, def]) => [key, await rasterizeBadge(def)])
    );
    return Object.fromEntries(entries);
};

/**
 * Fetches a remote image (e.g. a caller profile photo) and returns a circular,
 * cropped PNG data URL. Returns `null` on any failure (network, CORS, decode).
 * @param {string} url
 * @param {number} pixels Output resolution (square)
 * @returns {Promise<string|null>}
 */
export const fetchCircularImageDataUrl = async (url, pixels = 96) => {
    if (!url) return null;
    try {
        const img = await loadImage(url, 'anonymous');
        const canvas = document.createElement('canvas');
        canvas.width = pixels;
        canvas.height = pixels;
        const ctx = canvas.getContext('2d');
        ctx.save();
        ctx.beginPath();
        ctx.arc(pixels / 2, pixels / 2, pixels / 2, 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.clip();
        // Cover-fit the image within the circle.
        const ratio = Math.max(pixels / img.width, pixels / img.height);
        const drawW = img.width * ratio;
        const drawH = img.height * ratio;
        ctx.drawImage(img, (pixels - drawW) / 2, (pixels - drawH) / 2, drawW, drawH);
        ctx.restore();
        return canvas.toDataURL('image/png');
    } catch (error) {
        return null;
    }
};

/**
 * Renders a donut chart to a PNG data URL. Segments are drawn clockwise from the
 * top. When the total is zero, a neutral grey ring is drawn instead.
 * @param {Array<{value:number,color:string}>} segments
 * @param {number} pixels Output resolution (square)
 * @returns {string|null}
 */
export const renderDonutChartDataUrl = (segments = [], pixels = 260) => {
    try {
        const canvas = document.createElement('canvas');
        canvas.width = pixels;
        canvas.height = pixels;
        const ctx = canvas.getContext('2d');
        const cx = pixels / 2;
        const cy = pixels / 2;
        const r = pixels / 2 - 6;
        const total = segments.reduce((sum, seg) => sum + (Number(seg.value) || 0), 0);

        if (total <= 0) {
            ctx.beginPath();
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.fillStyle = '#E5E7EB';
            ctx.fill();
        } else {
            let start = -Math.PI / 2;
            segments.forEach((seg) => {
                const value = Number(seg.value) || 0;
                if (value <= 0) return;
                const angle = (value / total) * Math.PI * 2;
                ctx.beginPath();
                ctx.moveTo(cx, cy);
                ctx.arc(cx, cy, r, start, start + angle);
                ctx.closePath();
                ctx.fillStyle = seg.color;
                ctx.fill();
                start += angle;
            });
        }

        // Punch out the centre to create a donut.
        ctx.beginPath();
        ctx.arc(cx, cy, r * 0.58, 0, Math.PI * 2);
        ctx.fillStyle = '#FFFFFF';
        ctx.fill();
        return canvas.toDataURL('image/png');
    } catch (error) {
        return null;
    }
};
